import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface SubmissionErrorLog {
  id: string;
  event: string;
  errorCode: string;
  errorMessage: string;
  userId: string;
  isAdmin: boolean;
  adminMode: boolean;
  isUpdateAttempt: boolean;
  clientTimestamp: string;
  timestamp: Date | null;
}

const SubmissionErrorLogs: React.FC = () => {
  const [logs, setLogs] = useState<SubmissionErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError('');

        const logsRef = collection(db, 'submissionErrors');
        const logsQuery = query(logsRef, orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(logsQuery);

        const parsedLogs: SubmissionErrorLog[] = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const createdAt = data.timestamp?.toDate instanceof Function ? data.timestamp.toDate() : null;

          return {
            id: docSnap.id,
            event: String(data.event || 'Unknown'),
            errorCode: String(data.errorCode || 'unknown'),
            errorMessage: String(data.errorMessage || ''),
            userId: String(data.userId || ''),
            isAdmin: Boolean(data.isAdmin),
            adminMode: Boolean(data.adminMode),
            isUpdateAttempt: Boolean(data.isUpdateAttempt),
            clientTimestamp: String(data.clientTimestamp || ''),
            timestamp: createdAt
          };
        });

        setLogs(parsedLogs);
      } catch (fetchError) {
        console.error('Error fetching submission error logs:', fetchError);
        setError('Failed to load submission error logs');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return logs;
    }

    return logs.filter((log) => {
      return (
        log.event.toLowerCase().includes(normalized) ||
        log.errorCode.toLowerCase().includes(normalized) ||
        log.errorMessage.toLowerCase().includes(normalized) ||
        log.userId.toLowerCase().includes(normalized)
      );
    });
  }, [logs, searchTerm]);

  if (loading) {
    return <div>Loading submission error logs...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="report-section">
      <h2>Submission Error Logs</h2>
      <p className="report-description">
        Registration submission failures from the <code>submissionErrors</code> collection.
      </p>

      <div className="email-search-container">
        <input
          type="text"
          placeholder="Search by error code, user ID, event, or message..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="email-search-input"
        />
      </div>

      <div className="email-list-container">
        <table className="email-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Error Code</th>
              <th>User ID</th>
              <th>Context</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={5} className="security-empty-state">
                  No submission error log entries found.
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.timestamp ? log.timestamp.toLocaleString() : log.clientTimestamp || 'Unknown'}</td>
                  <td>{log.errorCode}</td>
                  <td>{log.userId || 'N/A'}</td>
                  <td>
                    {log.isUpdateAttempt ? 'Update' : 'Create'}
                    {log.adminMode ? ' / Admin Mode' : ''}
                    {log.isAdmin ? ' / Admin User' : ''}
                  </td>
                  <td>{log.errorMessage || 'No message'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubmissionErrorLogs;
